import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {HttpParams} from '@angular/common/http';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {ApiService} from '../api/api.service';
import {FloatingHelpService} from './floating-help.service';

export interface KnowledgeArticleSummary {
    id: number;
    title: string;
    summary: string | null;
    body?: string;
    status: string;
    category: { name: string } | null;
    tags: { slug: string; label: string; color: string | null }[];
}

export interface KnowledgeGroup {
    categoryName: string;
    articles: KnowledgeArticleSummary[];
}

@Component({
    selector: 'app-knowledge-sidenav',
    templateUrl: './knowledge-sidenav.component.html',
    styleUrls: ['./knowledge-sidenav.component.scss'],
})
export class KnowledgeSidenavComponent implements OnInit, OnDestroy {

    public open = false;
    public groups: KnowledgeGroup[] = [];
    public seleccionado: KnowledgeArticleSummary | null = null;

    private _subs: Subscription[] = [];
    private _helpKeys: string[] = [];

    constructor(
        private readonly _api: ApiService,
        private readonly _help: FloatingHelpService,
        private readonly _router: Router,
        private readonly _route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this._subs.push(this._help.open$.subscribe(open => {
            this.open = open;
            if (open) {
                this.seleccionado = null;
                this._cargarArticulos();
            }
        }));

        this._subs.push(this._router.events.pipe(filter(e => e instanceof NavigationEnd))
            .subscribe(() => {
                this._helpKeys = this._resolveHelpKeys();
                if (this.open) {
                    this._cargarArticulos();
                }
            }));

        this._helpKeys = this._resolveHelpKeys();
    }

    ngOnDestroy(): void {
        this._subs.forEach(s => s.unsubscribe());
    }

    public cerrar(): void {
        this._help.close();
    }

    public abrirArticulo(a: KnowledgeArticleSummary): void {
        this._api.get<KnowledgeArticleSummary>(`knowledge_articles/${a.id}`)
            .pipe(ApiService.sTakeBody())
            .subscribe(
                full => { this.seleccionado = full; },
                () => { this.seleccionado = a; }
            );
    }

    public volver(): void {
        this.seleccionado = null;
    }

    private _resolveHelpKeys(): string[] {
        let current = this._route.root;
        let helpKey: string | null = current.snapshot.data['helpKey'] ?? null;
        while (current.firstChild) {
            current = current.firstChild;
            if (current.snapshot.data['helpKey']) {
                helpKey = current.snapshot.data['helpKey'];
            }
        }
        return helpKey ? [helpKey] : this._resolveHelpKeysFromUrl();
    }

    /**
     * Fallback cuando ninguna ruta de la cadena declara data.helpKey: usa el primer
     * segmento de la URL (los módulos de dominio cuelgan de un segmento raíz estable,
     * p.ej. /materiales/materiales, /pedidos/historico) como helpKey general, y
     * además un helpKey compuesto primer-segundo segmento (p.ej. "solicitudes-intraoral",
     * "solicitudes-escaneo") para poder diferenciar sub-módulos dentro de un mismo
     * segmento raíz. Se consultan ambos con OR: un artículo tageado solo con el
     * genérico sigue apareciendo en todo el módulo; uno tageado con el compuesto
     * aparece solo en ese sub-módulo. Evita tener que anotar helpKey a mano en cada
     * ruta nueva.
     */
    private _resolveHelpKeysFromUrl(): string[] {
        const path = this._router.url.split('?')[0].split('#')[0];
        const segments = path.split('/').filter(segment => segment.length > 0);
        if (segments.length === 0) {
            return [];
        }
        const keys = [segments[0]];
        if (segments.length > 1) {
            keys.push(`${segments[0]}-${segments[1]}`);
        }
        return keys;
    }

    private _cargarArticulos(): void {
        let params = new HttpParams().set('status', 'published');
        for (const key of this._helpKeys) {
            params = params.append('tags.slug[]', key);
        }
        this._api.getCollection<KnowledgeArticleSummary>('knowledge_articles', params, 0, 100)
            .pipe(ApiService.sTakeBody())
            .subscribe(
                resp => { this.groups = this._agrupar(resp.member); },
                err => console.error('[Ayuda] Error cargando artículos:', err)
            );
    }

    private _agrupar(articles: KnowledgeArticleSummary[]): KnowledgeGroup[] {
        const map = new Map<string, KnowledgeArticleSummary[]>();
        for (const a of articles) {
            const key = a.category?.name ?? 'General';
            if (!map.has(key)) {
                map.set(key, []);
            }
            map.get(key)!.push(a);
        }
        return Array.from(map.entries()).map(([categoryName, arts]) => ({categoryName, articles: arts}));
    }
}
